import React, { useMemo, useRef, useState } from 'react';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';

import { useRegistrationUIContext } from '../RegistrationUIContext';
import { validFormsData } from '../RegistrationUIHelpers';

const passwordRules = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{5,}$/;

const Schema = Yup.object().shape({
  password: Yup.string()
    .required('Поле обязательно для заполнения')
    .min(8, 'Пароль должен состоять минимум из 8 символов')
    .matches(/[a-zA-Z]/, 'Пароль должен содержать только латинского буквы и цифры.')
    .matches(passwordRules, {
      message: 'Пароль должен содержать минимум 1 заглавную букву, 1 строчную букву и 1 цифру.',
    }),
  password_confirmation: Yup.string().oneOf([Yup.ref('password')], 'Пароли не совпадают'),
  agree: Yup.bool().oneOf([true], 'Вы должны согласиться с условиями'),
});

export const Step3 = () => {
  const formikRef = useRef(null);
  const [hasRussianLetters, setHasRussianLetters] = useState(false);
  const [passwordChecks, setPasswordChecks] = useState({
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    onlyLatin: true,
  });
  const [showRequirements, setShowRequirements] = useState(false);

  const registrationUIContext = useRegistrationUIContext();
  const registrationUIProps = useMemo(() => {
    return {
      validDataStep3: registrationUIContext.validDataStep3,
      setValidDataStep3: registrationUIContext.setValidDataStep3,
      formDataStep3: registrationUIContext.formDataStep3,
      setFormDataStep3: registrationUIContext.setFormDataStep3,
    };
  }, [
    registrationUIContext.validDataStep3,
    registrationUIContext.setValidDataStep3,
    registrationUIContext.formDataStep3,
    registrationUIContext.setFormDataStep3,
  ]);

  const checkPasswordRequirements = password => {
    const checks = {
      minLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      onlyLatin: !/[а-яА-ЯёЁ]/.test(password),
    };
    setPasswordChecks(checks);
    setHasRussianLetters(!checks.onlyLatin);

    if (password.length > 0 && !showRequirements) {
      setShowRequirements(true);
    }
  };

  const onValidate = e => {
    const value = e.target.type == 'checkbox' ? e.target.checked : e.target.value;

    if (e.target.name === 'password') {
      checkPasswordRequirements(value);
    }

    registrationUIProps.setFormDataStep3({
      ...registrationUIProps.formDataStep3,
      [e.target.name]: value,
    });
    formikRef.current.setFieldValue(e.target.name, value).then(() => {
      registrationUIProps.setValidDataStep3(formikRef.current.isValid);
      validFormsData.step3 = formikRef.current.isValid;
    });
  };

  const CheckIcon = ({ checked }) => (
    <span className={`svg-icon svg-icon-2 ${checked ? 'text-success' : 'text-muted'}`}>
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
        {checked ? (
          <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 0 1 .02-.022z" />
        ) : (
          <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.5" />
        )}
      </svg>
    </span>
  );

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{
        password: registrationUIProps.formDataStep3.password,
        password_confirmation: registrationUIProps.formDataStep3.password_confirmation,
        agree: registrationUIProps.formDataStep3.agree,
      }}
      validationSchema={Schema}
    >
      {({ errors, touched, values }) => (
        <Form className="form form-label-right">
          <div className="form-group fv-plugins-icon-container">
            <label className="font-size-h6 font-weight-bolder text-dark">Пароль</label>
            <Field
              type="password"
              placeholder="Введите пароль"
              name="password"
              onChange={e => onValidate(e)}
              className="form-control form-control-solid h-auto py-5 px-6 mr-2"
            />

            {hasRussianLetters && (
              <div className="text-danger mt-2 small d-flex align-items-center">
                В пароле обнаружены русские буквы. Используйте только латинские буквы.
              </div>
            )}

            {showRequirements && (
              <div className="mt-3">
                <div className="text-muted mb-2">Требования к паролю:</div>
                <ul className="list-unstyled mb-0">
                  <li className="d-flex align-items-center mb-1">
                    <CheckIcon checked={passwordChecks.minLength} />
                    <span className={`ml-2 ${passwordChecks.minLength ? 'text-success' : 'text-muted'}`}>
                      Не менее 8 символов
                    </span>
                  </li>
                  <li className="d-flex align-items-center mb-1">
                    <CheckIcon checked={passwordChecks.hasUpperCase} />
                    <span className={`ml-2 ${passwordChecks.hasUpperCase ? 'text-success' : 'text-muted'}`}>
                      Хотя бы одна заглавная буква (A-Z)
                    </span>
                  </li>
                  <li className="d-flex align-items-center mb-1">
                    <CheckIcon checked={passwordChecks.hasLowerCase} />
                    <span className={`ml-2 ${passwordChecks.hasLowerCase ? 'text-success' : 'text-muted'}`}>
                      Хотя бы одна строчная буква (a-z)
                    </span>
                  </li>
                  <li className="d-flex align-items-center mb-1">
                    <CheckIcon checked={passwordChecks.hasNumber} />
                    <span className={`ml-2 ${passwordChecks.hasNumber ? 'text-success' : 'text-muted'}`}>
                      Хотя бы одна цифра (0-9)
                    </span>
                  </li>
                  <li className="d-flex align-items-center mb-1">
                    <CheckIcon checked={passwordChecks.onlyLatin} />
                    <span className={`ml-2 ${passwordChecks.onlyLatin ? 'text-success' : 'text-muted'}`}>
                      Только латинские буквы
                    </span>
                  </li>
                </ul>
              </div>
            )}
          </div>

          <div className="form-group fv-plugins-icon-container">
            <label className="font-size-h6 font-weight-bolder text-dark">Повтор пароля</label>
            <Field
              type="password"
              placeholder="Повторите пароль"
              name="password_confirmation"
              onChange={e => onValidate(e)}
              className="form-control form-control-solid h-auto py-5 px-6 mr-2"
            />
          </div>

          <div className="form-group fv-plugins-icon-container">
            <label className="checkbox mb-0">
              <Field type="checkbox" name="agree" onChange={e => onValidate(e)} />
              <span></span>
              <div className="ml-2">
                Регистрируясь, Вы соглашаетесь с условиями{' '}
                <a target='_blank' href="https://atri-energo.ru/content/privacy_policy.html">политики конфиденциальности</a>.
              </div>
            </label>
          </div>
        </Form>
      )}
    </Formik>
  );
};
